import React, { useEffect, useState } from "react";
import {
  Container,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Box,
  Checkbox,
  Collapse,
  IconButton,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { get_services } from "../../services/services";
import { asyncStatus } from "../../utils/asyncStatus";
import { themeOrange } from "../../utils/colorTheme";
import { getImageUrl } from "../../utils/imageUrl";
import { service_booking_keys, steps_constants } from "../../store/constants";
import { setJobsSteps } from "../../store/slices/ui_control_slice";
import { MdExpandMore, MdExpandLess, MdOpenInNew, MdNotificationsNone, MdNotificationsActive } from "react-icons/md";
import {
  addCredits,
  deductCredits,
  getCreditBalance,
  CREDIT_PER_REMINDER_AMOUNT,
} from "../../utils/creditSystem";
import { success_toast_message } from "../../utils/toast_message";

// ─── Season config ──────────────────────────────────────────────────────────
const SEASONS = [
  {
    key: "spring",
    name: "Spring",
    months: "March – May",
    icon: "🌸",
    color: "#4CAF50",
    bg: "#f1f8f1",
    keywords: ["lawn", "garden", "yard clean", "eavestrough", "gutter", "window", "exterior caulk", "power", "wash", "pressure", "deck", "fence", "paint", "pest", "mulch", "tree", "stump", "artificial turf", "sod", "stone", "interlock", "patio", "concrete", "seasonal lawn", "seasonal"],
  },
  {
    key: "summer",
    name: "Summer",
    months: "June – August",
    icon: "☀️",
    color: "#FF9800",
    bg: "#fff8f0",
    keywords: ["ac tune", "tune-up", "tune up", "bbq", "barbecue", "grill", "lawn", "pest", "wasp", "hornet", "bee", "window", "handyman", "tv", "mount", "moving", "delivery"],
  },
  {
    key: "fall",
    name: "Fall",
    months: "September – November",
    icon: "🍂",
    color: "#E65100",
    bg: "#fff3ed",
    keywords: ["eavestrough", "gutter", "furnace", "heating", "hvac", "yard clean", "yard", "tree", "stump", "seasonal lawn", "seasonal", "window", "exterior caulk", "roof", "pest control annual", "annual"],
  },
  {
    key: "winter",
    name: "Winter",
    months: "December – February",
    icon: "❄️",
    color: "#1976D2",
    bg: "#f0f6ff",
    keywords: ["snow", "heating", "cooling", "hvac", "furnace", "smart home", "smart", "deep clean", "deep", "organiz", "declutter", "appliance", "handyman", "holiday", "light", "christmas"],
  },
];

const getCurrentSeason = () => {
  const month = new Date().getMonth() + 1;
  if (month >= 3 && month <= 5) return "spring";
  if (month >= 6 && month <= 8) return "summer";
  if (month >= 9 && month <= 11) return "fall";
  return "winter";
};

const matchServiceToSeasons = (serviceName) => {
  if (!serviceName) return [];
  const lower = serviceName.toLowerCase();
  return SEASONS.filter((season) =>
    season.keywords.some((kw) => lower.includes(kw))
  ).map((s) => s.key);
};

const CHECKLIST_KEY = "done_seasonal_checklist";
const loadChecklist = () => {
  try { return JSON.parse(localStorage.getItem(CHECKLIST_KEY) || "{}"); }
  catch { return {}; }
};
const saveChecklist = (data) => localStorage.setItem(CHECKLIST_KEY, JSON.stringify(data));

// ─── Season Section ─────────────────────────────────────────────────────────
const REMINDERS_KEY = "done_service_reminders";
const loadServiceReminders = () => {
  try { return JSON.parse(localStorage.getItem(REMINDERS_KEY) || "{}"); }
  catch { return {}; }
};
const saveServiceReminders = (data) => localStorage.setItem(REMINDERS_KEY, JSON.stringify(data));

const SeasonSection = ({ season, services, checked, onToggle, onNavigate, isCurrentSeason, onCreditEarned }) => {
  const [open, setOpen] = useState(isCurrentSeason);
  const [reminders, setReminders] = useState(loadServiceReminders);

  const total = services.length;
  const done = services.filter((s) => checked[s.id]).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const handleServiceBell = (serviceId, serviceName) => {
    const updated = { ...reminders };
    if (updated[serviceId]) {
      delete updated[serviceId];
      setReminders(updated);
      saveServiceReminders(updated);
      deductCredits(CREDIT_PER_REMINDER_AMOUNT);
      onCreditEarned();
    } else {
      updated[serviceId] = { season: season.key, name: serviceName, credited: true };
      setReminders(updated);
      saveServiceReminders(updated);
      // Award credit
      const seasonLabel = season.name;
      addCredits(CREDIT_PER_REMINDER_AMOUNT, `Reminder: ${serviceName} (${seasonLabel})`);
      success_toast_message(`$${CREDIT_PER_REMINDER_AMOUNT} credit earned! We'll remind you for ${serviceName} in ${seasonLabel}.`);
      onCreditEarned();
    }
  };

  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: `1.5px solid ${isCurrentSeason ? season.color : "#e0e0e0"}`,
        overflow: "hidden",
        backgroundColor: "white",
        boxShadow: isCurrentSeason ? `0 2px 12px ${season.color}25` : "none",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setOpen((p) => !p)}
        sx={{
          px: 3,
          py: 2,
          backgroundColor: open ? season.bg : "white",
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": { backgroundColor: season.bg },
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography sx={{ fontSize: "28px", lineHeight: 1 }}>{season.icon}</Typography>
          <Stack>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography sx={{ fontWeight: "700", fontSize: "18px", color: "#1a1a1a" }}>
                {season.name}
              </Typography>
              {isCurrentSeason && (
                <Chip
                  label="Current"
                  size="small"
                  sx={{
                    backgroundColor: season.color,
                    color: "white",
                    fontWeight: "600",
                    fontSize: "10px",
                    height: "20px",
                  }}
                />
              )}
            </Stack>
            <Typography sx={{ fontSize: "12px", color: "#888" }}>{season.months}</Typography>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.5}>
          <Stack alignItems="flex-end" gap={0.5} sx={{ minWidth: "80px" }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", color: season.color }}>
              {done}/{total} done
            </Typography>
            <Box sx={{ width: "80px" }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: `${season.color}20`,
                  "& .MuiLinearProgress-bar": { backgroundColor: season.color, borderRadius: 3 },
                }}
              />
            </Box>
          </Stack>
          <IconButton size="small" onClick={(e) => e.stopPropagation()}>
            {open ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
          </IconButton>
        </Stack>
      </Stack>

      {/* Service List */}
      <Collapse in={open}>
        <Stack divider={<Box sx={{ height: "1px", backgroundColor: "#f0f0f0" }} />}>
          {services.length === 0 ? (
            <Stack alignItems="center" py={3}>
              <Typography sx={{ color: "#aaa", fontSize: "14px" }}>No services found</Typography>
            </Stack>
          ) : (
            services.map((service) => {
              const isDone = !!checked[service.id];
              return (
                <Stack
                  key={service.id}
                  direction="row"
                  alignItems="center"
                  gap={2}
                  sx={{
                    px: 3,
                    py: 1.5,
                    backgroundColor: isDone ? "#fafafa" : "white",
                    transition: "background-color 0.2s",
                    "&:hover": { backgroundColor: season.bg },
                  }}
                >
                  <Checkbox
                    checked={isDone}
                    onChange={() => onToggle(service.id)}
                    sx={{
                      padding: "4px",
                      color: `${season.color}60`,
                      "&.Mui-checked": { color: season.color },
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                      opacity: isDone ? 0.5 : 1,
                    }}
                  >
                    <img
                      src={getImageUrl(service.mobile_image || service.image)}
                      alt={service.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Box>
                  <Stack flex={1} sx={{ opacity: isDone ? 0.5 : 1 }}>
                    <Typography
                      sx={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: isDone ? "#aaa" : "#1a1a1a",
                        textDecoration: isDone ? "line-through" : "none",
                        lineHeight: 1.3,
                      }}
                    >
                      {service.name}
                    </Typography>
                    {service.price && (
                      <Typography sx={{ fontSize: "13px", color: themeOrange, fontWeight: "500" }}>
                        From ${service.price}
                      </Typography>
                    )}
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleServiceBell(service.id, service.name); }}
                    title={reminders[service.id] ? "Reminder set" : `Set reminder & earn $${CREDIT_PER_REMINDER_AMOUNT}`}
                    sx={{
                      color: reminders[service.id] ? season.color : "#ccc",
                      border: `1px solid ${reminders[service.id] ? season.color + "60" : "#e0e0e0"}`,
                      borderRadius: "8px",
                      padding: "6px",
                      flexShrink: 0,
                      backgroundColor: reminders[service.id] ? `${season.color}10` : "transparent",
                      "&:hover": { backgroundColor: `${season.color}15`, color: season.color, borderColor: season.color },
                    }}
                  >
                    {reminders[service.id] ? <MdNotificationsActive size={16} /> : <MdNotificationsNone size={16} />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onNavigate(service.slug || service.id)}
                    sx={{
                      color: themeOrange,
                      border: `1px solid ${themeOrange}40`,
                      borderRadius: "8px",
                      padding: "6px",
                      flexShrink: 0,
                      "&:hover": { backgroundColor: `${themeOrange}10` },
                    }}
                  >
                    <MdOpenInNew size={16} />
                  </IconButton>
                </Stack>
              );
            })
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const ServicesPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentSeason = getCurrentSeason();

  const { get_all_services_status, get_all_services_data } = useSelector(
    (state) => state.serviceManage
  );

  const [checked, setChecked] = useState(loadChecklist);
  const [creditBalance, setCreditBalance] = useState(getCreditBalance);
  const isLoading = get_all_services_status === asyncStatus.LOADING;

  const handleCreditEarned = () => setCreditBalance(getCreditBalance());

  useEffect(() => {
    dispatch(get_services());
  }, [dispatch]);

  const handleToggle = (serviceId) => {
    setChecked((prev) => {
      const next = { ...prev, [serviceId]: !prev[serviceId] };
      if (!next[serviceId]) delete next[serviceId];
      saveChecklist(next);
      return next;
    });
  };

  const handleNavigate = (slugOrId) => {
    navigate(`/service-details/${slugOrId}`);
    dispatch(setJobsSteps({ value: null }));
    localStorage.removeItem(steps_constants.JOBS_STEP);
    localStorage.removeItem(service_booking_keys.DATE);
    localStorage.removeItem(service_booking_keys.TIME);
    localStorage.removeItem(service_booking_keys.DESCRIPTION);
    localStorage.removeItem(service_booking_keys.LOCATION);
    localStorage.removeItem(service_booking_keys.PRICE);
    localStorage.removeItem(service_booking_keys.IMAGES);
  };

  const services = get_all_services_data?.services || [];

  const servicesBySeason = SEASONS.reduce((acc, season) => {
    acc[season.key] = services.filter((s) =>
      matchServiceToSeasons(s.name).includes(season.key)
    );
    return acc;
  }, {});

  const totalAll = services.length;
  const doneAll = services.filter((s) => checked[s.id]).length;

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
        <CircularProgress size={40} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  return (
    <Stack bgcolor="#FAFAFA" minHeight="100vh">
      <Container maxWidth="xl">
        <Stack spacing={3} pt={3} pb={5}>
          {/* Header */}
          <Stack spacing={1}>
            <Typography className="mainHeading">Services</Typography>
            <hr color="#D1D1D1" />
          </Stack>

          {/* Progress + Credit */}
          {totalAll > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #071d5b 0%, #0d2d7a 100%)",
                  color: "white",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                  <Stack>
                    <Typography sx={{ fontWeight: "700", fontSize: "17px" }}>
                      Home Maintenance Checklist
                    </Typography>
                    <Typography sx={{ fontSize: "12px", opacity: 0.8, mt: 0.5 }}>
                      Track seasonal services you've booked or completed
                    </Typography>
                  </Stack>
                  <Stack alignItems="flex-end" gap={0.5}>
                    <Typography sx={{ fontWeight: "700", fontSize: "20px" }}>
                      {doneAll}/{totalAll}
                    </Typography>
                    <Box sx={{ width: "120px" }}>
                      <LinearProgress
                        variant="determinate"
                        value={totalAll > 0 ? Math.round((doneAll / totalAll) * 100) : 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "rgba(255,255,255,0.2)",
                          "& .MuiLinearProgress-bar": { backgroundColor: themeOrange, borderRadius: 4 },
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: "11px", opacity: 0.7 }}>
                      {totalAll > 0 ? Math.round((doneAll / totalAll) * 100) : 0}% complete
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {creditBalance > 0 && (
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    backgroundColor: "white",
                    border: "1.5px solid #f0f0f0",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    minWidth: { xs: "100%", sm: "180px" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: "#888", fontWeight: 600 }}>
                    Your Credits
                  </Typography>
                  <Typography sx={{ fontWeight: "800", fontSize: "28px", color: themeOrange, lineHeight: 1.2, mt: 0.5 }}>
                    ${creditBalance.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}

          {/* Season Sections */}
          <Stack spacing={2}>
            {SEASONS.map((season) => (
              <SeasonSection
                key={season.key}
                season={season}
                services={servicesBySeason[season.key] || []}
                checked={checked}
                onToggle={handleToggle}
                onNavigate={handleNavigate}
                isCurrentSeason={season.key === currentSeason}
                onCreditEarned={handleCreditEarned}
              />
            ))}
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};

export default ServicesPage;
